// Shared DrawReady scoring — single source of truth for per-item draw readiness.
// Mirrors the logic used by the per-phase chips in ConstructionPhasesV2 so the
// draw-request gate and the phase list never drift apart.

export const SMALL_ITEM_THRESHOLD = 2500;

export const categoryOf = (p) => {
  const t = p?.phase_type;
  return (t === 'material' || t === 'labor' || t === 'labor_material' || t === 'soft_cost') ? t : 'labor';
};

// Per-item requirements. Users pick what's required; smart defaults apply when
// unset: labor / labor & materials require W-9, COI, lien waiver, final invoice
// and (for trades) a passed inspection — but items under $2,500 skip COI & lien.
// Materials / soft costs require just an invoice.
export function effectiveRequirements(p) {
  const cat = categoryOf(p);
  const isLabor = cat === 'labor' || cat === 'labor_material';
  const amount = (p.estimated_cost || p.actual_cost || 0);
  const small = amount > 0 && amount < SMALL_ITEM_THRESHOLD;
  const def = isLabor
    ? { w9: true, coi: !small, lien: !small, invoice: true, inspection: !!p.requires_inspection }
    : { w9: false, coi: false, lien: false, invoice: true, inspection: false };
  const pick = (flag, d) => (flag === true || flag === false ? flag : d);
  return {
    w9: pick(p.req_w9, def.w9),
    coi: pick(p.req_coi, def.coi),
    lien: pick(p.req_lien, def.lien),
    invoice: pick(p.req_invoice, def.invoice),
    inspection: pick(p.req_inspection, def.inspection),
  };
}

// DrawReady score for one item: only requirements kept ON are counted, plus a
// budget factor (a draw isn't guaranteed if over budget unless contingency has funds).
export function computeDrawReadiness(p, contractor, contingencyRemaining = 0) {
  const req = effectiveRequirements(p);
  const est = p.estimated_cost || 0;
  const act = p.actual_cost || 0;
  const overBudget = act > est && est > 0;
  const budgetDone = !overBudget || contingencyRemaining > 0;
  const budgetLabel = !overBudget
    ? 'On / under budget'
    : (contingencyRemaining > 0 ? 'Over budget — covered by contingency' : 'Over budget — no contingency left');
  const coiOk = !!(contractor && contractor.coi_received)
    && (!contractor.coi_expiration || new Date(contractor.coi_expiration) > new Date());
  const hasInvoice = p.has_receipt
    || (Array.isArray(p.receipts) && p.receipts.length > 0)
    || p.is_final_payment || p.payment_status === 'final' || !!p.payment_proof_file_id
    || !!p.receipt_file_id || !!p.invoice_file_id || (parseFloat(p.invoice_amount) > 0);
  const inspPassed = p.inspection_passed || (Array.isArray(p.inspections) && p.inspections.some((i) => i.status === 'passed'));
  const steps = [];
  if (req.w9) steps.push({ key: 'w9', label: 'W-9 on file', done: !!(contractor && contractor.w9_received) });
  if (req.coi) steps.push({ key: 'coi', label: 'Valid COI', done: coiOk });
  if (req.lien) steps.push({ key: 'lien', label: 'Lien waiver signed', done: !!p.lien_waiver_signed });
  if (req.invoice) steps.push({ key: 'invoice', label: 'Invoice uploaded', done: !!hasInvoice });
  if (req.inspection) steps.push({ key: 'inspection', label: 'Passed inspection', done: !!inspPassed });
  steps.push({ key: 'budget', label: budgetLabel, done: budgetDone });
  const done = steps.filter((s) => s.done).length;
  const score = steps.length ? Math.round((done / steps.length) * 100) : 0;
  return { score, ready: score === 100, steps };
}

// Aggregate readiness across the items in a single draw request.
export function aggregateDrawReadiness(phases, contractorsById = {}, contingencyRemaining = 0) {
  const perItem = (phases || []).map((p) => {
    const contractor = p.contractor_id ? contractorsById[p.contractor_id] : null;
    const r = computeDrawReadiness(p, contractor, contingencyRemaining);
    return {
      id: p.id,
      name: p.name || 'Line item',
      score: r.score,
      ready: r.ready,
      steps: r.steps,
      missing: r.steps.filter((s) => !s.done).map((s) => s.label),
    };
  });
  const totalSteps = perItem.reduce((s, it) => s + it.steps.length, 0);
  const doneSteps = perItem.reduce((s, it) => s + it.steps.filter((x) => x.done).length, 0);
  const score = totalSteps ? Math.round((doneSteps / totalSteps) * 100) : 100;
  const itemsNeedingAttention = perItem.filter((it) => !it.ready).length;
  return { score, ready: score === 100, totalSteps, doneSteps, itemsNeedingAttention, perItem };
}
